import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { styled } from '@linaria/react';
import { showOverlay } from 'modules/overlay/slice';
import { OverlayType } from 'modules/overlay/types';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { Divider } from '@daedalus/atlas/Divider';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Text } from '@daedalus/atlas/Text';
import { useInView } from '@daedalus/core/src/_web/utils/browser/hooks/useInView';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { ContentHotel } from '@findhotel/sapi';
import { CheckInCheckOut } from '../Policies/CheckInCheckOut';
import { PolicySubSection } from '../Policies/types';
import { getDescription, getTitle, isSubSectionAvailable, policySubSections } from '../Policies/utils';
const _exp = /*#__PURE__*/() => Flex.Column;
const PolicyItemWrapper = /*#__PURE__*/styled(_exp())({
  name: "PolicyItemWrapper",
  class: "p1cwvwuf",
  propsAsIs: true
});
const _exp2 = /*#__PURE__*/() => Text;
const PolicyTitle = /*#__PURE__*/styled(_exp2())({
  name: "PolicyTitle",
  class: "p2pstvz",
  propsAsIs: true
});
const _exp4 = /*#__PURE__*/() => ButtonLink;
const PolicyButton = /*#__PURE__*/styled(_exp4())({
  name: "PolicyButton",
  class: "p109qck6",
  propsAsIs: true
});
const _exp6 = /*#__PURE__*/() => ({
  hasCheckInInstructions
}) => hasCheckInInstructions ? 1 : '0 0 50%';
const CheckInCheckOutWrapper = /*#__PURE__*/styled('div')({
  name: "CheckInCheckOutWrapper",
  class: "c1yy7or3",
  propsAsIs: false,
  vars: {
    "c1yy7or3-0": [_exp6()]
  }
});
const PolicyItem = ({
  section,
  hotel,
  dataId
}: {
  section: PolicySubSection;
  hotel: ContentHotel;
  dataId?: string;
}) => {
  const {
    formatMessage
  } = useIntl();
  const dispatch = useDispatch();
  const dispatchTeamEvent = useDispatchTeamEvent();
  const showSubSection = () => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsPropertyPolicy,
      action: Action.Clicked,
      payload: {
        selectedElement: section
      }
    }));
    dispatch(showOverlay({
      type: OverlayType.HotelPolicies,
      params: {
        policySubSection: section
      }
    }));
  };
  const showDepositAmount = section === PolicySubSection.SecurityDeposit && Boolean(hotel.deposit?.value);
  return <PolicyItemWrapper alignItems="flex-start">
      <PolicyTitle variant="titleL" dataId={dataId}>
        {formatMessage(getTitle(section))}
      </PolicyTitle>
      <Text variant="bodyM">
        {showDepositAmount ? `${hotel.deposit?.value?.currency} ${hotel.deposit?.value?.amount} ` : null}
        {formatMessage(getDescription(section, showDepositAmount))}
      </Text>
      <PolicyButton variant="default" size="lg" isCompact onClick={showSubSection}>
        <FormattedMessageWrapper id="showMore" defaultMessage="Show more" />
      </PolicyButton>
    </PolicyItemWrapper>;
};
interface Props {
  hotel: ContentHotel;
}
export const PoliciesSection: React.FC<Props> = ({
  hotel
}) => {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  const dispatchTeamEvent = useDispatchTeamEvent();
  useEffect(() => {
    if (inView) dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.HotelDetailsPropertyPolicySection,
      action: Action.Viewed
    }));
  }, [dispatchTeamEvent, inView]);
  const policySubSectionsKeys = Object.keys(policySubSections).filter((section: PolicySubSection) => isSubSectionAvailable(hotel, section));
  const hasCheckInInstructions = isSubSectionAvailable(hotel, PolicySubSection.CheckInInstructions);
  return <div ref={ref}>
      <ContentWrapper paddingBottom="s900">
        <Flex.Column gap="s600">
          <Text variant="titleXL">
            <FormattedMessageWrapper defaultMessage="Property policies" id="propertyPolicy" />
          </Text>

          <Flex.Column gap="s800">
            <Flex.Row justifyContent={hasCheckInInstructions ? 'space-between' : 'flex-start'}>
              {hasCheckInInstructions && <PolicyItem section={PolicySubSection.CheckInInstructions} hotel={hotel} />}
              {hotel.checkInTime && hotel.checkOutTime && <CheckInCheckOutWrapper hasCheckInInstructions={hasCheckInInstructions}>
                  <CheckInCheckOut isLoading={false} layout="secondary" hotel={hotel} />
                </CheckInCheckOutWrapper>}
            </Flex.Row>

            {policySubSectionsKeys.length > 0 && <>
                <Divider />
                <Flex.Row justifyContent="space-between" gap={policySubSectionsKeys.length > 3 ? 's900' : 's000'}>
                  {policySubSectionsKeys.map((section: PolicySubSection) => section !== PolicySubSection.CheckInInstructions && <PolicyItem key={section} hotel={hotel} section={section} dataId={section} />)}
                </Flex.Row>
              </>}
          </Flex.Column>
        </Flex.Column>
      </ContentWrapper>
    </div>;
};

require("./PoliciesSection.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PoliciesSection.tsx");